/**
 * WorksSeoMeta.js
 *
 * SEO + structured data component for the "3 Simple Steps" process section.
 * Mirrors WelcomeSeoMeta.js's patterns: keyed <Helmet> so meta always flushes
 * on change, a word-boundary-safe description trimmer, and JSON-LD structured
 * data appropriate to the content — here a HowTo (3-step process) instead of
 * Welcome's WebApplication schema.
 *
 * NOTE ON EMBEDDING: <Works /> is typically rendered as a section inside a
 * larger page (id="testimonial"), not its own route. react-helmet-async
 * dedupes singleton tags like <title>/<link rel="canonical">, so whichever
 * <Helmet> renders last "wins". If Works is nested inside a page that
 * already sets its own title/canonical (e.g. the homepage), either drop
 * <WorksSeoMeta /> from that usage or pass overrides — the JSON-LD block is
 * always safe to keep since multiple <script type="application/ld+json">
 * tags can coexist.
 */

import React from "react";
import { Helmet } from "react-helmet-async";

// ─── Constants ─────────────────────────────────────────────────────────────────
const SITE_NAME     = "Yoga Shastra";
const SITE_URL      = "https://www.yoga-shastra.com";
const OG_IMAGE       = `${SITE_URL}/og-image-how-it-works.jpg`;
const TWITTER_HANDLE = "@yogashastra";
const PAGE_PATH      = "/how-it-works";

// ─── Safe description trimmer — respects word boundaries ──────────────────────
const trimDesc = (str = "", max = 155) => {
  if (!str || str.length <= max) return str;
  return str.slice(0, max).replace(/\s+\S*$/, "").trim() + "…";
};

// ═══════════════════════════════════════════════════════════════════════════════
// WorksPageMeta
// ═══════════════════════════════════════════════════════════════════════════════

export const WorksPageMeta = ({
  title: titleOverride,
  description: descriptionOverride,
  steps = [],
} = {}) => {
  const title = titleOverride
    ? `${titleOverride} – ${SITE_NAME}`
    : `How It Works — Start Your Yoga Journey in 3 Steps | ${SITE_NAME}`;

  const description = trimDesc(
    descriptionOverride ||
      "From choosing your program to meeting your instructor, see how easy it is to get started with live online yoga at Yoga Shastra."
  );

  const canonical = `${SITE_URL}${PAGE_PATH}`;

  // ── HowTo JSON-LD — one step per card, in display order ─────────────────────
  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to Get Started with Yoga Shastra",
    description,
    step: steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.comments,
    })),
  };

  // ── BreadcrumbList JSON-LD ───────────────────────────────────────────────────
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "How It Works", item: canonical },
    ],
  };

  // Keyed so Helmet reliably flushes if title/description ever change client-side
  return (
    <Helmet key={canonical}>
      {/* ── Primary meta ── */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* ── Open Graph ── */}
      <meta property="og:type"         content="website" />
      <meta property="og:site_name"    content={SITE_NAME} />
      <meta property="og:title"        content={title} />
      <meta property="og:description"  content={description} />
      <meta property="og:url"          content={canonical} />
      <meta property="og:image"        content={OG_IMAGE} />
      <meta property="og:image:width"  content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale"       content="en_IN" />

      {/* ── Twitter card ── */}
      <meta name="twitter:card"        content="summary_large_image" />
      <meta name="twitter:site"        content={TWITTER_HANDLE} />
      <meta name="twitter:title"       content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image"       content={OG_IMAGE} />

      {/* ── Structured data ── */}
      {steps.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(howToSchema)}
        </script>
      )}
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbSchema)}
      </script>
    </Helmet>
  );
};
